import { useState, useEffect, useCallback, useRef } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl } from '../../lib/api'
import { getStorageMode } from '../../lib/storageMode'

/** Fetch for Explorer only — sends auth token + storage mode */
function explorerFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
  const headers: Record<string, string> = {}
  if (init.headers) Object.assign(headers, init.headers)
  if (token) headers['Authorization'] = `Bearer ${token}`
  headers['X-Storage-Mode'] = getStorageMode()
  return fetch(url, { ...init, headers })
}

interface TreeNode {
  name: string
  path: string
  type: 'file' | 'folder'
  children?: TreeNode[]
}

const LANG_COLORS: Record<string, string> = {
  tsx: '#61dafb', ts: '#3178c6', js: '#f7df1e', jsx: '#61dafb',
  json: '#f5d76e', css: '#c07ef5', html: '#e34c26', md: '#9395b0',
  py: '#3572a5', rs: '#ce422b', go: '#00add8', env: '#f5a157',
}

function extOf(name: string): string {
  const parts = name.split('.')
  return parts.length > 1 ? (parts.at(-1) ?? '') : ''
}

export function Sidebar() {
  const [tree, setTree] = useState<TreeNode[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [selectedFolder, setSelectedFolder] = useState<string>('')
  const [creating, setCreating] = useState<{ type: 'file' | 'folder'; parentPath: string } | null>(null)
  const [newName, setNewName] = useState('')
  const [dragOver, setDragOver] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: TreeNode } | null>(null)
  const [renaming, setRenaming] = useState<{ node: TreeNode; newName: string } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  const { openTab } = useAppStore()

  const fetchTree = useCallback(async () => {
    try {
      const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
      if (!token) { setLoading(false); return }
      const res = await explorerFetch(apiUrl('/api/files/list'))
      if (res.ok) {
        const data = await res.json()
        setTree(Array.isArray(data) ? data : [])
      }
    } catch {}
    setLoading(false)
  }, [])

  useEffect(() => {
    const timer = setTimeout(fetchTree, 300)
    return () => clearTimeout(timer)
  }, [fetchTree])

  useEffect(() => {
    const handler = () => {
      console.log('[Sidebar] storage mode changed, refetching...')
      setTimeout(fetchTree, 300)
    }
    window.addEventListener('pulse:files-changed', handler)
    window.addEventListener('pulse:storage-mode-changed', handler)
    return () => {
      window.removeEventListener('pulse:files-changed', handler)
      window.removeEventListener('pulse:storage-mode-changed', handler)
    }
  }, [fetchTree])

  useEffect(() => {
    if (creating) setTimeout(() => inputRef.current?.focus(), 50)
  }, [creating])

  const toggle = (p: string) =>
    setExpanded((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n })

  const openFile = async (node: TreeNode) => {
    if (node.type !== 'file') return
    try {
      const res = await explorerFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(node.path)}`))
      const data = await res.json()
      openTab({
        id: node.path,
        path: `/${node.path}`,
        name: node.name,
        language: extOf(node.name) || 'text',
        content: data.content ?? '',
      })
    } catch {
      openTab({
        id: node.path,
        path: `/${node.path}`,
        name: node.name,
        language: extOf(node.name) || 'text',
        content: '',
      })
    }
  }

  const handleSelectFolder = (folderPath: string) => {
    setSelectedFolder(folderPath)
    // Auto-expand so user sees where they're creating
    setExpanded((s) => new Set(s).add(folderPath))
  }

  const startCreate = (type: 'file' | 'folder') => {
    // If user hasn't explicitly clicked a folder, create at root
    setCreating({ type, parentPath: selectedFolder })
    setNewName('')
    if (selectedFolder) {
      setExpanded((s) => new Set(s).add(selectedFolder))
    }
  }

  // Click on empty space in tree = deselect folder (create at root)
  const handleTreeClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedFolder('')
    }
  }

  // Clear button in header to reset to root
  const handleClearFolder = () => setSelectedFolder('')

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name || !creating) { setCreating(null); setNewName(''); return }

    const fullPath = creating.parentPath ? `${creating.parentPath}/${name}` : name

    if (creating.type === 'file') {
      try {
        await explorerFetch(apiUrl('/api/files/write'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: fullPath, content: '' }),
        })
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
        openTab({
          id: fullPath,
          path: `/${fullPath}`,
          name: name.split('/').at(-1) ?? name,
          language: extOf(name) || 'text',
          content: '',
        })
      } catch {}
    } else {
      try {
        await explorerFetch(apiUrl('/api/files/mkdir'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: fullPath }),
        })
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
        setExpanded((s) => new Set(s).add(fullPath))
      } catch {}
    }

    setCreating(null)
    setNewName('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCreate()
    if (e.key === 'Escape') { setCreating(null); setNewName('') }
  }

  // Delete file or folder
  const handleDelete = async (node: TreeNode) => {
    // Only protect index.html inside project folders (e.g. task-manager/index.html)
    if (node.name === 'index.html' && node.path.includes('/')) {
      alert('Cannot delete index.html inside a project — it is the app entry point.')
      setContextMenu(null)
      return
    }
    const label = node.type === 'folder' ? `folder "${node.name}" and all its contents` : `file "${node.name}"`
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return

    try {
      await explorerFetch(apiUrl('/api/files/delete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: node.path }),
      })
      const tab = useAppStore.getState().openTabs.find((t) => t.path === `/${node.path}` || t.id === node.path)
      if (tab) useAppStore.getState().closeTab(tab.id)
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
    } catch {}
    setContextMenu(null)
  }

  // Rename file or folder
  const handleStartRename = (node: TreeNode) => {
    setRenaming({ node, newName: node.name })
    setContextMenu(null)
    setTimeout(() => renameRef.current?.focus(), 50)
  }

  const handleRename = async () => {
    if (!renaming || !renaming.newName.trim() || renaming.newName === renaming.node.name) {
      setRenaming(null)
      return
    }
    const oldPath = renaming.node.path
    const parts = oldPath.split('/')
    parts[parts.length - 1] = renaming.newName.trim()
    const newPath = parts.join('/')

    try {
      const res = await explorerFetch(apiUrl('/api/files/move'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: oldPath, to: newPath }),
      })
      if (res.ok) {
        // Update open tab if renamed file is open
        const tab = useAppStore.getState().openTabs.find((t) => t.path === `/${oldPath}` || t.id === oldPath)
        if (tab) {
          useAppStore.getState().closeTab(tab.id)
          openTab({ id: newPath, path: `/${newPath}`, name: renaming.newName.trim(), language: extOf(renaming.newName) || 'text', content: tab.content })
        }
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      }
    } catch {}
    setRenaming(null)
  }

  // Download file
  const handleDownload = async (node: TreeNode) => {
    if (node.type !== 'file') return
    try {
      const res = await explorerFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(node.path)}`))
      const data = await res.json()
      const blob = new Blob([data.content ?? ''], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = node.name; a.click()
      URL.revokeObjectURL(url)
    } catch {}
    setContextMenu(null)
  }

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return
    const handler = () => setContextMenu(null)
    window.addEventListener('click', handler)
    return () => window.removeEventListener('click', handler)
  }, [contextMenu])

  // Drag and drop: move file/folder
  const handleDrop = async (targetFolderPath: string, e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(null)
    const sourcePath = e.dataTransfer.getData('text/plain')
    if (!sourcePath || sourcePath === targetFolderPath) return

    const fileName = sourcePath.split('/').at(-1) ?? sourcePath
    const newPath = targetFolderPath ? `${targetFolderPath}/${fileName}` : fileName

    if (newPath === sourcePath) return

    try {
      const res = await explorerFetch(apiUrl('/api/files/move'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: sourcePath, to: newPath }),
      })
      if (res.ok) {
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      }
    } catch {}
  }

  // Determine where the inline input should appear
  const showInputAtRoot = creating && !creating.parentPath

  return (
    <div className="sidebar">
      <div className="sidebar__header">
        <span>EXPLORER</span>
        {localStorage.getItem('pulse-collab-room') && <span style={{ fontSize: 8, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', marginLeft: 6 }}>🔴 LIVE</span>}
        <div style={{ display: 'flex', gap: 2 }}>
          {selectedFolder && (
            <button className="sidebar__icon-btn" title="Create at root (deselect folder)" onClick={handleClearFolder} style={{ fontSize: 9, color: 'var(--accent)' }}>/</button>
          )}
          <button className="sidebar__icon-btn" title="New File" onClick={() => startCreate('file')}>📄</button>
          <button className="sidebar__icon-btn" title="New Folder" onClick={() => startCreate('folder')}>📁</button>
          <button className="sidebar__icon-btn" title="Refresh" onClick={fetchTree}>↻</button>
        </div>
      </div>

      {/* Selected folder indicator */}
      {selectedFolder && (
        <div style={{
          padding: '3px 14px',
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          color: 'var(--accent)',
          background: 'rgba(124,110,245,0.06)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>📂 {selectedFolder}</span>
          <button
            onClick={() => setSelectedFolder('')}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}
          >✕</button>
        </div>
      )}

      <div
        className="sidebar__tree"
        onClick={handleTreeClick}
        onDragOver={(e) => { e.preventDefault(); setDragOver('') }}
        onDragLeave={() => setDragOver(null)}
        onDrop={(e) => handleDrop('', e)}
        style={{ background: dragOver === '' ? 'rgba(124,110,245,0.04)' : undefined }}
      >
        {loading && <div style={{ padding: 12, opacity: 0.5 }}>Loading…</div>}
        {!loading && tree.length === 0 && !creating && (
          <div style={{ padding: 12, opacity: 0.5, fontSize: 10, lineHeight: 1.6 }}>
            Empty workspace — use the terminal or click 📄 to create files
          </div>
        )}

        {/* Root-level inline input */}
        {showInputAtRoot && (
          <CreateInput
            inputRef={inputRef}
            type={creating.type}
            value={newName}
            onChange={setNewName}
            onKeyDown={handleKeyDown}
            onBlur={handleCreate}
          />
        )}

        {tree.map((node) => (
          <TreeItem
            key={node.path}
            node={node}
            depth={0}
            expanded={expanded}
            selectedFolder={selectedFolder}
            creating={creating}
            newName={newName}
            inputRef={inputRef}
            dragOver={dragOver}
            renaming={renaming}
            renameRef={renameRef}
            onToggle={toggle}
            onOpen={openFile}
            onSelectFolder={handleSelectFolder}
            onSetNewName={setNewName}
            onKeyDown={handleKeyDown}
            onBlur={handleCreate}
            onDragOver={setDragOver}
            onDrop={handleDrop}
            onContextMenu={(e, n) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, node: n }) }}
            onRenameChange={(v) => setRenaming(renaming ? { ...renaming, newName: v } : null)}
            onRenameSubmit={handleRename}
            onRenameCancel={() => setRenaming(null)}
          />
        ))}
      </div>

      <div className="sidebar__git">
        <span className="sidebar__git-dot" />
        <span>workspace</span>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div style={{
          position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 999,
          background: 'var(--bg-1)', border: '1px solid var(--border-2)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          minWidth: 140, overflow: 'hidden', padding: '4px 0',
        }}>
          {contextMenu.node.type === 'file' && (
            <button onClick={() => handleDownload(contextMenu.node)} style={ctxMenuBtn}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download
            </button>
          )}
          <button onClick={() => handleStartRename(contextMenu.node)} style={ctxMenuBtn}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            Rename
          </button>
          <button onClick={() => handleDelete(contextMenu.node)} style={{ ...ctxMenuBtn, color: 'var(--red)' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

interface CreateInputProps {
  type: 'file' | 'folder'
  value: string
  inputRef: React.RefObject<HTMLInputElement | null>
  onChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onBlur: () => void
}

function CreateInput({ type, value, inputRef, onChange, onKeyDown, onBlur }: CreateInputProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 14px' }}>
      <span style={{ fontSize: 12 }}>{type === 'file' ? '📄' : '📁'}</span>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={type === 'file' ? 'filename.js' : 'folder-name'}
        style={{
          flex: 1, background: 'var(--bg-3)', border: '1px solid var(--accent)',
          borderRadius: 4, color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
          fontSize: 11, padding: '2px 6px', outline: 'none',
        }}
      />
    </div>
  )
}

function TreeItem({
  node, depth, expanded, selectedFolder, creating, newName, inputRef, dragOver,
  renaming, renameRef,
  onToggle, onOpen, onSelectFolder, onSetNewName, onKeyDown, onBlur, onDragOver, onDrop, onContextMenu,
  onRenameChange, onRenameSubmit, onRenameCancel,
}: {
  node: TreeNode
  depth: number
  expanded: Set<string>
  selectedFolder: string
  creating: { type: 'file' | 'folder'; parentPath: string } | null
  newName: string
  inputRef: React.RefObject<HTMLInputElement | null>
  dragOver: string | null
  renaming: { node: TreeNode; newName: string } | null
  renameRef: React.RefObject<HTMLInputElement | null>
  onToggle: (p: string) => void
  onOpen: (node: TreeNode) => void
  onSelectFolder: (p: string) => void
  onSetNewName: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onBlur: () => void
  onDragOver: (p: string | null) => void
  onDrop: (targetFolder: string, e: React.DragEvent) => void
  onContextMenu: (e: React.MouseEvent, node: TreeNode) => void
  onRenameChange: (v: string) => void
  onRenameSubmit: () => void
  onRenameCancel: () => void
}) {
  const isOpen = expanded.has(node.path)
  const ext = extOf(node.name)
  const color = LANG_COLORS[ext] ?? 'var(--text-1)'
  const isSelected = selectedFolder === node.path
  const isDragTarget = dragOver === node.path
  const isRenaming = renaming?.node.path === node.path

  const showInputHere = creating && creating.parentPath === node.path && node.type === 'folder' && isOpen

  const handleClick = () => {
    if (node.type === 'folder') {
      onToggle(node.path)
      onSelectFolder(node.path)
    } else {
      onOpen(node)
    }
  }

  return (
    <>
      <div
        className={`tree-item ${node.type === 'folder' ? 'tree-item--folder' : ''}`}
        style={{
          paddingLeft: 14 + depth * 14,
          background: isSelected
            ? 'rgba(124,110,245,0.1)'
            : isDragTarget
              ? 'rgba(95,212,164,0.1)'
              : undefined,
          borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
        }}
        onClick={handleClick}
        onContextMenu={(e) => onContextMenu(e, node)}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', node.path)
          e.dataTransfer.effectAllowed = 'move'
        }}
        onDragOver={(e) => {
          if (node.type === 'folder') {
            e.preventDefault()
            e.stopPropagation()
            onDragOver(node.path)
          }
        }}
        onDragLeave={(e) => {
          e.stopPropagation()
          onDragOver(null)
        }}
        onDrop={(e) => {
          if (node.type === 'folder') {
            e.stopPropagation()
            onDrop(node.path, e)
          }
        }}
      >
        <span className="tree-item__chevron">
          {node.type === 'folder' ? (isOpen ? '▾' : '▸') : ''}
        </span>
        <span className="tree-item__icon" style={{ color }}>
          {node.type === 'folder' ? '📁' : '◻'}
        </span>
        <span className="tree-item__name">
          {isRenaming ? (
            <input
              ref={renameRef}
              value={renaming.newName}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onRenameSubmit(); if (e.key === 'Escape') onRenameCancel() }}
              onBlur={onRenameSubmit}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--bg-3)', border: '1px solid var(--accent)',
                borderRadius: 3, color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
                fontSize: 11, padding: '1px 4px', outline: 'none', width: '100%',
              }}
            />
          ) : node.name}
        </span>
      </div>

      {node.type === 'folder' && isOpen && (
        <>
          {/* Inline create input inside this folder */}
          {showInputHere && (
            <div style={{ paddingLeft: 14 + (depth + 1) * 14 }}>
              <CreateInput
                inputRef={inputRef}
                type={creating.type}
                value={newName}
                onChange={onSetNewName}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
              />
            </div>
          )}

          {node.children?.map((child) => (
            <TreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedFolder={selectedFolder}
              creating={creating}
              newName={newName}
              inputRef={inputRef}
              dragOver={dragOver}
              renaming={renaming}
              renameRef={renameRef}
              onToggle={onToggle}
              onOpen={onOpen}
              onSelectFolder={onSelectFolder}
              onSetNewName={onSetNewName}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              onRenameChange={onRenameChange}
              onRenameSubmit={onRenameSubmit}
              onRenameCancel={onRenameCancel}
            />
          ))}
        </>
      )}
    </>
  )
}


const ctxMenuBtn: React.CSSProperties = {
  width: '100%', padding: '7px 14px', background: 'none', border: 'none',
  color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  fontSize: 11, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
  transition: 'background 0.08s',
}
